/**
 * Storage Adapter - Cross-platform storage abstraction
 *
 * Supports:
 * - Web: localStorage
 * - React Native: AsyncStorage
 */

import { createJSONStorage, type StateStorage } from 'zustand/middleware';

// Storage interface that both implementations must follow
export interface StorageAdapter {
  getItem(key: string): Promise<string | null> | string | null;
  setItem(key: string, value: string): Promise<void> | void;
  removeItem(key: string): Promise<void> | void;
}

// Web implementation (localStorage) - always available
const webStorage: StorageAdapter = {
  getItem: (key: string) => localStorage.getItem(key),
  setItem: (key: string, value: string) => localStorage.setItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(key),
};

/**
 * Zustand-compatible storage wrapper for Web
 * Works synchronously with localStorage
 * Returns a PersistStorage compatible object via createJSONStorage
 */
export const createZustandStorage = () => {
  const storage: StateStorage = {
    getItem: async (key: string): Promise<string | null> => {
      try {
        // Check if running in React Native environment
        if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
          // In React Native, AsyncStorage must be provided externally
          // This will be overridden by mobile-specific implementation
          throw new Error('Use createZustandStorageNative() in React Native');
        }

        // Web: use localStorage
        const value = localStorage.getItem(key);
        return value;
      } catch (error) {
        console.error(`Error reading from storage (key: ${key}):`, error);
        return null;
      }
    },
    setItem: async (key: string, value: string): Promise<void> => {
      try {
        if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
          throw new Error('Use createZustandStorageNative() in React Native');
        }

        localStorage.setItem(key, value);
      } catch (error) {
        console.error(`Error writing to storage (key: ${key}):`, error);
      }
    },
    removeItem: async (key: string): Promise<void> => {
      try {
        if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
          throw new Error('Use createZustandStorageNative() in React Native');
        }

        localStorage.removeItem(key);
      } catch (error) {
        console.error(`Error removing from storage (key: ${key}):`, error);
      }
    },
  };

  return createJSONStorage(() => storage);
};

/**
 * Create storage for React Native
 * Must be called with AsyncStorage passed from mobile app
 * Returns a PersistStorage compatible object via createJSONStorage
 *
 * Usage in React Native:
 * ```
 * import AsyncStorage from '@react-native-async-storage/async-storage';
 * const storage = createZustandStorageNative(AsyncStorage);
 * ```
 */
export const createZustandStorageNative = (AsyncStorage: any) => {
  const storage: StateStorage = {
    getItem: async (key: string): Promise<string | null> => {
      try {
        const value = await AsyncStorage.getItem(key);
        return value;
      } catch (error) {
        console.error(`Error reading from AsyncStorage (key: ${key}):`, error);
        return null;
      }
    },
    setItem: async (key: string, value: string): Promise<void> => {
      try {
        await AsyncStorage.setItem(key, value);
      } catch (error) {
        console.error(`Error writing to AsyncStorage (key: ${key}):`, error);
      }
    },
    removeItem: async (key: string): Promise<void> => {
      try {
        await AsyncStorage.removeItem(key);
      } catch (error) {
        console.error(`Error removing from AsyncStorage (key: ${key}):`, error);
      }
    },
  };

  return createJSONStorage(() => storage);
};
